// ── Quarterly OKR (AI flow) — coach side: enums, limits and copy ───────────
//
// Pure data and pure helpers, no React. The enum VALUES are the contract's and
// match the CEO app's `UserPanel/QuarterlyOkr/_config/quarterlyOkrConfig.js`
// one for one — the live review components are shared between the two apps
// and read these names. Only what the coach screens use lives here.
//
// Contract: `31-quarterly-okr-fe-handoff.md` §9 (coach portal), §10 (live
// review), §12 (summary), §16 (enums), §17 (errors). It wins on any
// disagreement.

// ─── Run states (§2.1) ──────────────────────────────────────────────────────
export const RUN_STATE = {
  COMPANY_DRAFT_IN_PROGRESS: "COMPANY_DRAFT_IN_PROGRESS",
  COMPANY_DRAFT_GENERATED: "COMPANY_DRAFT_GENERATED",
  COMPANY_INTERVIEW_IN_PROGRESS: "COMPANY_INTERVIEW_IN_PROGRESS",
  GUIDANCE_NOTES: "GUIDANCE_NOTES",
  INDIVIDUAL_OKRS: "INDIVIDUAL_OKRS",
  SUBMITTED_FOR_COACH: "SUBMITTED_FOR_COACH",
  COACH_REVIEW_IN_PROGRESS: "COACH_REVIEW_IN_PROGRESS",
  COACH_REVIEW_COMPLETED: "COACH_REVIEW_COMPLETED",
  LIVE_REVIEW_SCHEDULED: "LIVE_REVIEW_SCHEDULED",
  LIVE_REVIEW_IN_PROGRESS: "LIVE_REVIEW_IN_PROGRESS",
  LOCKED: "LOCKED",
  FROZEN: "FROZEN",
  ARCHIVED: "ARCHIVED",
};

// The review is the coach's to work on in these states (§9.3)…
export const REVIEWABLE_STATES = [
  RUN_STATE.SUBMITTED_FOR_COACH,
  RUN_STATE.COACH_REVIEW_IN_PROGRESS,
];

// …and has been sent to the CEO in these; "Start live review" is offered from
// either (§9.5).
export const REVIEW_SENT_STATES = [
  RUN_STATE.COACH_REVIEW_COMPLETED,
  RUN_STATE.LIVE_REVIEW_SCHEDULED,
];

// After the lock, or once the quarter closed unlocked, only the summary is left.
export const SUMMARY_STATES = [
  RUN_STATE.LOCKED,
  RUN_STATE.FROZEN,
  RUN_STATE.ARCHIVED,
];

// The coach's wording for each stage on the dashboard — "sent to you", not
// "with your coach".
export const RUN_STATE_LABEL = {
  [RUN_STATE.COMPANY_DRAFT_IN_PROGRESS]: "Drafting company OKRs",
  [RUN_STATE.COMPANY_DRAFT_GENERATED]: "Company OKRs drafted",
  [RUN_STATE.COMPANY_INTERVIEW_IN_PROGRESS]: "CEO setting company OKRs",
  [RUN_STATE.GUIDANCE_NOTES]: "CEO writing guidance notes",
  [RUN_STATE.INDIVIDUAL_OKRS]: "Team setting individual OKRs",
  [RUN_STATE.SUBMITTED_FOR_COACH]: "Sent to you",
  [RUN_STATE.COACH_REVIEW_IN_PROGRESS]: "Review started",
  [RUN_STATE.COACH_REVIEW_COMPLETED]: "Review sent to CEO",
  [RUN_STATE.LIVE_REVIEW_SCHEDULED]: "Live review scheduled",
  [RUN_STATE.LIVE_REVIEW_IN_PROGRESS]: "Live review started",
  [RUN_STATE.LOCKED]: "Locked",
  [RUN_STATE.FROZEN]: "Not completed",
  [RUN_STATE.ARCHIVED]: "Archived",
};

// How the run reached the coach (§8, §16).
export const SEND_MODE = { AUTOMATIC: "AUTOMATIC", EARLY: "EARLY" };

// ─── Members (§2.2, §16) ────────────────────────────────────────────────────
export const MEMBER_STATE = {
  WAITING_FOR_NOTE: "WAITING_FOR_NOTE",
  READY_TO_START: "READY_TO_START",
  DRAFT_IN_PROGRESS: "DRAFT_IN_PROGRESS",
  DRAFT_GENERATED: "DRAFT_GENERATED",
  INTERVIEW_IN_PROGRESS: "INTERVIEW_IN_PROGRESS",
  SUBMITTED: "SUBMITTED",
  CLOSED_NOT_SUBMITTED: "CLOSED_NOT_SUBMITTED",
  JOINED_AFTER_SEND: "JOINED_AFTER_SEND",
};

export const MEMBER_STATE_LABEL = {
  [MEMBER_STATE.WAITING_FOR_NOTE]: "Waiting for a note",
  [MEMBER_STATE.READY_TO_START]: "Not started",
  [MEMBER_STATE.DRAFT_IN_PROGRESS]: "Drafting",
  [MEMBER_STATE.DRAFT_GENERATED]: "Draft ready",
  [MEMBER_STATE.INTERVIEW_IN_PROGRESS]: "In interview",
  [MEMBER_STATE.SUBMITTED]: "Submitted",
  [MEMBER_STATE.CLOSED_NOT_SUBMITTED]: "Not submitted",
  [MEMBER_STATE.JOINED_AFTER_SEND]: "Joined after send",
};

// How an objective reached the coach without its member submitting (§9.3).
export const SUBMISSION_KIND = {
  MEMBER: "MEMBER",
  AI_DRAFT_ON_EARLY_SEND: "AI_DRAFT_ON_EARLY_SEND",
  UNSUBMITTED_DRAFT_ON_EARLY_SEND: "UNSUBMITTED_DRAFT_ON_EARLY_SEND",
};

export const isNotSubmittedByMember = (kind) =>
  kind === SUBMISSION_KIND.AI_DRAFT_ON_EARLY_SEND ||
  kind === SUBMISSION_KIND.UNSUBMITTED_DRAFT_ON_EARLY_SEND;

// ─── Objectives ─────────────────────────────────────────────────────────────
export const OBJECTIVE_TYPE = { COMPANY: "company", INDIVIDUAL: "individual" };

// One key per objective across both types (`company:501`, `individual:901`),
// for marks maps and "focus this row" ids.
export const objectiveKey = (type, objectiveId) => `${type}:${objectiveId}`;

// §16 objective `workflow_status`.
export const WORKFLOW_STATUS = {
  AI_DRAFTED: "AI_DRAFTED",
  SUBMITTED: "SUBMITTED",
  REVIEWED: "REVIEWED",
  FINALIZED: "FINALIZED",
};

// Coach marks (§9.3).
export const REVIEW_STATUS = { ACCEPTED: "accepted", TO_DISCUSS: "to_discuss" };

// Comment scopes (§9.3) — exactly one target per scope.
export const COMMENT_SCOPE = {
  OBJECTIVE: "objective",
  MEMBER: "member",
  COMPANY: "company",
};

// ─── Summary (§12) ──────────────────────────────────────────────────────────
export const SUMMARY_STATUS = {
  LOCKED: "locked",
  FROZEN: "frozen",
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  ARCHIVED: "archived",
};

export const SUMMARY_TAG_LABEL = {
  draft_never_finalised: "Draft — never finalised",
  finalised_not_locked: "Finalised — not locked",
  draft: "Draft",
  submitted_not_locked: "Submitted — not locked",
};

// ─── Limits (§16) ───────────────────────────────────────────────────────────
export const LIMITS = {
  OBJECTIVE_MAX_CHARS: 500,
  KEY_RESULT_MAX_CHARS: 300,
  KEY_RESULTS_MIN: 1,
  KEY_RESULTS_MAX: 5,
  INITIATIVE_MAX_CHARS: 300,
  // At least one initiative per objective (2026-10-21, by instruction —
  // checked everywhere KRs / initiatives are added or removed).
  INITIATIVES_MIN: 1,
  INITIATIVES_MAX: 15,
  // The DTOs accept 4000 but the service caps comments at 2000 (§18.7).
  COMMENT_MAX_CHARS: 2000,
  MEETING_DURATION_MIN: 1,
  MEETING_DURATION_MAX: 600,
};

// ─── Coach brief (handoff 34) ───────────────────────────────────────────────
export const BRIEF_STATUS = {
  IN_PROGRESS: "IN_PROGRESS",
  READY: "READY",
  FAILED: "FAILED",
};

// The dashboard chip per `brief_status` (null → no chip).
export const BRIEF_CHIP = {
  [BRIEF_STATUS.READY]: { tone: "teal", label: "Brief ready" },
  [BRIEF_STATUS.IN_PROGRESS]: { tone: "grey", label: "Preparing brief" },
  [BRIEF_STATUS.FAILED]: { tone: "red", label: "Brief not available" },
};

// ─── Error copy (§17) ───────────────────────────────────────────────────────
// Keyed by the error `code`. Screens fall back to the server's `message`.
export const ERROR_COPY = {
  quarter_not_found: "We couldn't find that quarter.",
  run_not_found: "We couldn't find that quarter.",
  forbidden: "You don't have access to this client's Quarterly OKRs.",
  invalid_state: "This quarter has moved on. The page is refreshed.",
  quarter_frozen: "This quarter's planning was frozen when the quarter closed.",
  quarter_archived: "This quarter was archived.",
  not_connected: "You're offline — reconnecting. Try again in a moment.",
  // Review (§9.3)
  review_not_in_progress: "This review isn't open. The page is refreshed.",
  invalid_status: "Choose Accept or To discuss.",
  invalid_comment: "A comment needs 1–2000 characters.",
  comment_not_found: "That comment no longer exists. The page is refreshed.",
  objective_not_found:
    "That objective no longer exists. The page is refreshed.",
  unmarked_objectives: "Accept or flag every objective before sending.",
  invalid_meeting: "Pick both a date and a time for the live review.",
  invalid_duration: "The live review can last 1 to 600 minutes.",
  already_submitted: "This review was already sent to the CEO.",
  // Live review (§10)
  live_review_not_started: "The live review hasn't started yet.",
  live_review_not_in_progress: "The live review isn't running any more.",
  not_authorized: "You're not part of this live review.",
  read_only: "Only the CEO and the coach can change OKRs in the live review.",
  stale_card: "Someone changed this objective — reload and try again.",
  review_not_found: "This objective has no review mark to discuss.",
  to_discuss_remaining: "Discuss every to-discuss objective before locking.",
  already_locked: "This quarter is already locked.",
  not_joined_after:
    "Only people who joined after the send can be drafted here.",
  already_has_objectives: "This person already has objectives.",
  // Coach brief (handoff 34)
  brief_in_progress: "The brief is already being written.",
  regenerate_not_allowed:
    "The brief can't be regenerated once the live review has started.",
};

// A failed request → `{ code, message, data }`. Errors carry `code` and any
// extra fields (`unmarked`, `run_state`…) on the response body, like the CEO
// app reads them; `message` prefers our copy over the server's.
export const readError = (error) => {
  const data = error?.response?.data || {};
  return {
    code: data.code || null,
    message:
      ERROR_COPY[data.code] ||
      data.message ||
      "Something went wrong. Please try again in a moment.",
    data,
  };
};
